import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsIncome } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterComparison, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '../utils/const'
import { ASSET_FIELD } from '@/const'

import { DuckDBService } from '@/services/duckdb'

import { getFilterTags, getFilterValues } from '../utils/helpers'
import { getCurrentDate } from '@/helpers/dates'

import { useTagsBunchStore } from '@/store/tags/bunch'

import useAnalyticsStore from '../index'

export const TABLE_NAME = 'analytics_income'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [AnalyticsField.CATEGORY_ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.TAGS]: 'VARCHAR[]',
  [AnalyticsField.AMOUNT]: 'DOUBLE',
}

export default defineStore('analytics-income', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const datesRange = ref<string[]>([])

  // GETTERS

  const isInit = computed(() => deprecated.value !== undefined)

  const getDatesRange = computed(() => datesRange.value)

  // SETTERS

  const setList = async (data: {
    income_view: Record<string, AnalyticsIncome[]>
  }) => {
    if (deprecated.value === false) return
    if (!data.income_view) return
    deprecated.value = false
    datesRange.value = Object.keys(data.income_view ?? {})
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await Promise.all(
      Object.values(data.income_view ?? {}).map(income =>
        duckdbInstance.pushData(TABLE_NAME, income, TABLE_SCHEMA),
      ),
    )
    trigger.value++
  }

  // ACTIONS

  const clear = () => {
    datesRange.value = []
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,

    setList,

    clear,
  }
})

type AnalyticsIncomeFiltersProps = {
  withDate?: boolean
  shortAccountType?: boolean
}

export const useAnalyticsIncomeFilters = ({ withDate = false, shortAccountType = false }: AnalyticsIncomeFiltersProps = {}) => {

  const analyticsStore = useAnalyticsStore()
  const tagsBunchStore = useTagsBunchStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues(TABLE_NAME, analyticsDate, undefined, tagsBunchStore)
  const getTags = getFilterTags(TABLE_NAME, analyticsDate, tagsBunchStore)

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT],
        getter: (context, comparison) => getValues(AnalyticsField.ACCOUNT_ID, undefined, context, comparison)
      },
      {
        name: AnalyticsField.ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT_TYPE],
        getter: (context, comparison) => getValues(AnalyticsField.ACCOUNT_ID, ASSET_FIELD.TYPE, context, comparison)
      },
      {
        name: AnalyticsField.ASSET_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: (context, comparison) => getValues(AnalyticsField.ASSET_ID, undefined, context, comparison)
      },
      {
        name: AnalyticsField.ASSET_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET_TYPE],
        getter: (context, comparison) => getValues(AnalyticsField.ASSET_ID, ASSET_FIELD.TYPE, context, comparison)
      },
      {
        name: AnalyticsField.CATEGORY_ACCOUNT_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.CATEGORY_ACCOUNT],
        getter: (context, comparison) => getValues(AnalyticsField.CATEGORY_ACCOUNT_ID, undefined, context, comparison)
      },
      {
        name: AnalyticsField.CATEGORY_ACCOUNT_TYPE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.CATEGORY_ACCOUNT_TYPE],
        getter: async (context, comparison) => {
          const result = await getValues(AnalyticsField.CATEGORY_ACCOUNT_ID, ASSET_FIELD.TYPE, context, comparison)
          return shortAccountType ? result.map(({ value, label }) => ({ value: value.replace(' account', ''), label })) : result
        }
      },
      {
        name: AnalyticsField.CATEGORY,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.CATEGORY],
        getter: (context, comparison) => getTags(AnalyticsField.CATEGORY, context, comparison)
      },
      {
        name: AnalyticsField.SUBCATEGORY,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.SUBCATEGORY],
        getter: (context, comparison) => getTags(AnalyticsField.SUBCATEGORY, context, comparison)
      },
      {
        name: AnalyticsField.AMOUNT,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.AMOUNT],
        defaultComparison: FilterComparison.EQ,
        isAmount: true
      }
    ]
    if (withDate) {
      result.push({
        name: AnalyticsField.DATE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.DATE],
        isDate: true
      })
    }
    tagsBunchStore.getMetricTags?.forEach(name => {
      result.push({
        name,
        caption: name,
        getter: (context, comparison) => getTags(name, context, comparison),
      })
    })
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
